/**
 * DSM Components — Behavior Scripts
 *
 * 此文件负责有交互行为的 DSM 组件。
 * 纯样式组件（Button、Badge、Tag）使用 dsm-components.css + dsm-*.liquid 实现。
 *
 * 包含：
 *   DsmDatepicker — Datepicker 日期选择器控制器
 *   DsmDialog — Dialog 对话框控制器
 */

/* ============================================================
   DsmToast — 轻提示控制器
   Figma: https://www.figma.com/design/DaaNEpELVLq1oWo540GsFW/DSM?node-id=4303-1433

   架构（参考 Element Plus message 前向链式堆叠）：
     - 每条 toast 独立 position:fixed，JS 计算 top/bottom 写入 inline style
     - 算法：offset(n) = offset(n-1) + height(n-1) + gap；第一条 = initialOffset
     - ResizeObserver 监听各条高度变化 → 自动触发后续条目重排
     - 关闭一条后从数组删除 → 重新计算后续偏移 → CSS transition 平滑位移

   API：
     DsmToast.show(options)              → toastId (string)
     DsmToast.dismiss(id)                → void
     DsmToast.closeAll(placement?)       → 关闭某 placement 所有，或全部

   options：
     message      {string}   必填
     tone         {string}   default | success | warning | error（默认 default）
     placement    {string}   top-right(默认) | top-left | top-center
                             bottom-right | bottom-left | bottom-center
     offset       {number}   距视口边缘初始距离 px（默认 16）
     gap          {number}   两条 toast 间距 px（默认 8）
     duration     {number}   自动消失 ms；0 = 不自动消失
                             默认：default/success 3000，warning/error 5000，带 action 至少 5000
     showClose    {boolean}  是否显示关闭按钮（默认 true）
     action       {string}   action 按钮文字（可选）
     onAction     {Function} action 点击回调（可选）
     onClose      {Function} 关闭后回调（可选）
     grouping     {boolean}  相同 message+tone 去重合并，计数 badge（默认 false）
     appendTo     {Element|string} 挂载目标，默认 document.body

   自定义事件（toast 元素上冒泡）：
     dsm:toast:show    { id, options }
     dsm:toast:dismiss { id }
   ============================================================ */

/* ============================================================
   DsmDialog - Dialog controller
   Controls .dsm-overlay / .dsm-dialog open/close, focus trap, ESC close

   API - static dialog (Liquid pre-rendered):
     DsmDialog.open('my-dialog')
     DsmDialog.close('my-dialog')

   API - dynamic dialog (JS-created, auto-destroyed on close):
     DsmDialog.open({
       title:          'Remove this item?',
       desc:           'This will be removed.',
       confirmLabel:   'Remove',
       confirmType:    'theme',
       cancelLabel:    'Cancel',
       stack:          false,
       closeOnOverlay: true,
       onConfirm:      function() { ... },
       onCancel:       function() { ... },
     })

   HTML trigger (no JS needed):
     <button data-dsm-dialog-open="my-dialog">Open</button>

   Custom events (on overlay element):
     dsm:dialog:confirm / dsm:dialog:cancel / dsm:dialog:open / dsm:dialog:close
   ============================================================ */

const DsmDialog = (() => {
  let _activeOverlay = null;
  let _previousFocus = null;

  function _getFocusable(container) {
    return Array.from(
      container.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), ' +
        'select:not([disabled]), textarea:not([disabled]), ' +
        '[tabindex]:not([tabindex="-1"])'
      )
    ).filter((el) => !el.closest('[hidden]') && !el.closest('[aria-hidden="true"]'));
  }

  function _trapFocus(e) {
    if (!_activeOverlay || e.key !== 'Tab') return;
    const focusable = _getFocusable(_activeOverlay);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }

  function _onKeydown(e) {
    if (e.key === 'Escape' && _activeOverlay) {
      const id = _activeOverlay.dataset.dsmDialog;
      _activeOverlay.dispatchEvent(new CustomEvent('dsm:dialog:cancel', {
        bubbles: true
      }));
      close(id);
    }
    _trapFocus(e);
  }

  function _onOverlayClick(e) {
    if (e.target !== e.currentTarget) return;
    if (e.currentTarget.hasAttribute('data-dsm-no-overlay-close')) return;
    var id = e.currentTarget.dataset.dsmDialog;
    e.currentTarget.dispatchEvent(new CustomEvent('dsm:dialog:cancel', {
      bubbles: true
    }));
    close(id);
  }

  function _escHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function _buildOverlay(opts) {
    var id = 'dsm-dialog-dynamic-' + Date.now();
    var title = opts.title || '';
    var desc = opts.desc || '';
    var confirmLabel = opts.confirmLabel || 'Confirm';
    var confirmType = opts.confirmType || 'primary';
    var cancelLabel = opts.cancelLabel || '';
    var stack = opts.stack ? ' dsm-dialog__footer--stack' : '';

    var cancelHtml = cancelLabel ?
      '<button class="dsm-btn dsm-btn--secondary dsm-btn--medium js-dsm-dialog-cancel" type="button" style="border-radius:999px;min-width:108px;">' +
      '<span class="dsm-btn__label">' + _escHtml(cancelLabel) + '</span>' +
      '</button>' :
      '';

    var confirmHtml =
      '<button class="dsm-btn dsm-btn--' + confirmType + ' dsm-btn--medium js-dsm-dialog-confirm" type="button" style="border-radius:999px;min-width:108px;">' +
      '<span class="dsm-btn__label">' + _escHtml(confirmLabel) + '</span>' +
      '</button>';

    var descHtml = desc ?
      '<div class="dsm-dialog__body"><p class="dsm-dialog__desc" id="' + id + '-desc">' + _escHtml(desc) + '</p></div>' :
      '';

    var overlay = document.createElement('div');
    overlay.className = 'dsm-overlay';
    overlay.id = id + '-overlay';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-labelledby', id + '-title');
    overlay.setAttribute('data-dsm-dialog', id);
    overlay.setAttribute('data-dsm-dynamic', '');
    if (opts.closeOnOverlay === false) overlay.setAttribute('data-dsm-no-overlay-close', '');
    if (desc) overlay.setAttribute('aria-describedby', id + '-desc');

    overlay.innerHTML =
      '<div class="dsm-dialog" role="document">' +
      '<div class="dsm-dialog__header">' +
      '<p class="dsm-dialog__title" id="' + id + '-title">' + _escHtml(title) + '</p>' +
      '</div>' +
      descHtml +
      '<div class="dsm-dialog__footer' + stack + '">' +
      cancelHtml +
      confirmHtml +
      '</div>' +
      '<button class="dsm-dialog__close js-dsm-dialog-cancel" type="button" aria-label="Close dialog">' +
      '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">' +
      '<line x1="18" y1="6" x2="6" y2="18"/>' +
      '<line x1="6" y1="6" x2="18" y2="18"/>' +
      '</svg>' +
      '</button>' +
      '</div>';

    if (typeof opts.onConfirm === 'function') {
      overlay._dsmOnConfirm = opts.onConfirm;
    }
    if (typeof opts.onCancel === 'function') {
      overlay.addEventListener('dsm:dialog:cancel', opts.onCancel, {
        once: true
      });
    }

    overlay.addEventListener('dsm:dialog:close', function () {
      setTimeout(function () {
        if (overlay.parentNode) overlay.parentNode.removeChild(overlay);
      }, 250);
    }, {
      once: true
    });

    document.body.appendChild(overlay);
    return id;
  }

  function _setLoading(btn, loading) {
    if (loading) {
      btn.classList.add('is-loading');
      btn.setAttribute('disabled', '');
      btn.setAttribute('aria-busy', 'true');
    } else {
      btn.classList.remove('is-loading');
      btn.removeAttribute('disabled');
      btn.removeAttribute('aria-busy');
    }
  }

  function open(idOrOpts) {
    var triggerEl = document.activeElement;
    if (typeof idOrOpts === 'object' && idOrOpts !== null) {
      var id = _buildOverlay(idOrOpts);
      requestAnimationFrame(function () {
        _activate(id, triggerEl);
      });
    } else {
      _activate(idOrOpts, triggerEl);
    }
  }

  function _activate(id, triggerEl) {
    var overlay = document.querySelector('[data-dsm-dialog="' + id + '"]');
    if (!overlay) {
      console.warn('[DsmDialog] No dialog found with id: "' + id + '"');
      return;
    }
    _previousFocus = (triggerEl && triggerEl !== document.body) ? triggerEl : document.activeElement;
    document.documentElement.classList.add('dsm-scroll-lock');
    overlay.style.removeProperty('display');
    overlay.classList.add('is-open');
    _activeOverlay = overlay;

    var focusable = _getFocusable(overlay);
    if (focusable.length) {
      var closeBtn = overlay.querySelector('.dsm-dialog__close');
      (closeBtn || focusable[0]).focus();
    }

    document.addEventListener('keydown', _onKeydown);
    overlay.addEventListener('click', _onOverlayClick);
    overlay.dispatchEvent(new CustomEvent('dsm:dialog:open', {
      bubbles: true
    }));
  }

  function close(id) {
    var overlay = document.querySelector('[data-dsm-dialog="' + id + '"]');
    if (!overlay) return;
    overlay.classList.remove('is-open');
    _activeOverlay = null;
    document.documentElement.classList.remove('dsm-scroll-lock');
    document.removeEventListener('keydown', _onKeydown);
    overlay.removeEventListener('click', _onOverlayClick);
    if (_previousFocus && typeof _previousFocus.focus === 'function' && _previousFocus.isConnected !== false) {
      _previousFocus.focus();
    }
    _previousFocus = null;
    overlay.dispatchEvent(new CustomEvent('dsm:dialog:close', {
      bubbles: true
    }));
  }

  function init() {
    document.addEventListener('click', function (e) {
      var trigger = e.target.closest('[data-dsm-dialog-open]');
      if (trigger) {
        e.preventDefault();
        open(trigger.dataset.dsmDialogOpen);
      }
    });

    document.addEventListener('click', function (e) {
      var btn = e.target.closest('.js-dsm-dialog-confirm');
      if (!btn) return;
      var overlay = btn.closest('[data-dsm-dialog]');
      if (!overlay) return;
      var id = overlay.dataset.dsmDialog;
      var asyncConfirm = overlay._dsmOnConfirm;

      if (typeof asyncConfirm === 'function') {
        var result = asyncConfirm();
        if (result && typeof result.then === 'function') {
          _setLoading(btn, true);
          result.then(
            function () {
              _setLoading(btn, false);
              overlay.dispatchEvent(new CustomEvent('dsm:dialog:confirm', {
                bubbles: true
              }));
              close(id);
            },
            function () {
              _setLoading(btn, false);
            }
          );
          return;
        }
      }
      overlay.dispatchEvent(new CustomEvent('dsm:dialog:confirm', {
        bubbles: true
      }));
      close(id);
    });

    document.addEventListener('click', function (e) {
      var btn = e.target.closest('.js-dsm-dialog-cancel');
      if (!btn) return;
      var overlay = btn.closest('[data-dsm-dialog]');
      if (!overlay) return;
      overlay.dispatchEvent(new CustomEvent('dsm:dialog:cancel', {
        bubbles: true
      }));
      close(overlay.dataset.dsmDialog);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  return {
    open: open,
    close: close
  };
})();

window.DsmDialog = DsmDialog;

/* ============================================================ */
const DsmToast = (() => {
  var DEFAULT_DURATION = {
    'default': 3000,
    'success': 3000,
    'warning': 5000,
    'error': 5000
  };
  var DEFAULT_OFFSET = 16;
  var DEFAULT_GAP = 8;
  var EXIT_MS = 220;

  var VALID_PLACEMENTS = [
    'top-right', 'top-left', 'top-center',
    'bottom-right', 'bottom-left', 'bottom-center'
  ];
  var DEFAULT_PLACEMENT = 'top-right';

  /*
   * _instances: placement → Array<entry>
   * entry = { id, el, height, offset, opts, timerId, remaining, startedAt, ro }
   */
  var _instances = {};
  var _idCounter = 0;

  var ICONS = {
    success: '<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><circle cx="10" cy="10" r="10" fill="currentColor"/><path d="M5.5 10.5L8.5 13.5L14.5 7.5" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    warning: '<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><circle cx="10" cy="10" r="10" fill="currentColor"/><path d="M10 6V11" stroke="white" stroke-width="1.5" stroke-linecap="round"/><circle cx="10" cy="13.5" r="0.75" fill="white"/></svg>',
    error: '<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><circle cx="10" cy="10" r="10" fill="currentColor"/><path d="M7 7L13 13M13 7L7 13" stroke="white" stroke-width="1.5" stroke-linecap="round"/></svg>'
  };
  var CLOSE_ICON = '<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M5 5L15 15M15 5L5 15" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>';

  /* ── 取 placement 的实例列表 ── */
  function _getList(placement) {
    if (!_instances[placement]) _instances[placement] = [];
    return _instances[placement];
  }

  /* ── 解析 appendTo ── */
  function _resolveContainer(appendTo) {
    if (!appendTo) return document.body;
    if (typeof appendTo === 'string') {
      var el = document.querySelector(appendTo);
      return el || document.body;
    }
    return appendTo;
  }

  /* ── 水平方向 inline style ── */
  function _hStyle(placement, offset) {
    if (placement.endsWith('right')) return 'right:' + offset + 'px;';
    if (placement.endsWith('left')) return 'left:' + offset + 'px;';
    /* center */
    return 'left:50%;transform:translateX(-50%);';
  }

  /* ── 垂直方向属性名 ── */
  function _vProp(placement) {
    return placement.startsWith('bottom') ? 'bottom' : 'top';
  }

  /* ── 计算指定 placement 中第 idx 条的 top/bottom 值 ── */
  function _calcOffset(list, idx, initialOffset, gap) {
    if (idx === 0) return initialOffset;
    var prev = list[idx - 1];
    return prev.offset + prev.height + gap;
  }

  /* ── 重新计算某 placement 所有条目的位置并写入 inline style ── */
  function _reflow(placement) {
    var list = _getList(placement);
    var vProp = _vProp(placement);
    for (var i = 0; i < list.length; i++) {
      var entry = list[i];
      var initialOffset = entry.opts.offset !== undefined ? entry.opts.offset : DEFAULT_OFFSET;
      var gap = entry.opts.gap !== undefined ? entry.opts.gap : DEFAULT_GAP;
      var newOffset = _calcOffset(list, i, initialOffset, gap);
      if (newOffset !== entry.offset) {
        entry.offset = newOffset;
        entry.el.style[vProp] = newOffset + 'px';
      }
    }
  }

  /* ── 构建 DOM ── */
  function _buildEl(id, opts, placement) {
    var tone = opts.tone || 'default';
    var showClose = opts.showClose !== false;
    var hasAction = !!opts.action;
    var hasIcon = tone !== 'default';
    var isBottom = placement.startsWith('bottom');
    var isCenter = placement.endsWith('center');

    var el = document.createElement('div');
    el.id = 'dsm-toast-' + id;
    el.className = [
      'dsm-toast',
      'dsm-toast--' + tone,
      isBottom ? 'dsm-toast--from-bottom' : '',
      isCenter ? 'dsm-toast--center' : (placement.endsWith('right') ? 'dsm-toast--right' : 'dsm-toast--left'),
      'dsm-toast--entering'
    ].join(' ').replace(/\s+/g, ' ').trim();

    el.setAttribute('role', 'status');
    el.setAttribute('aria-atomic', 'true');
    el.setAttribute('aria-live', 'polite');

    /* 水平定位（inline style，CSS class 负责 left/right/center transform） */
    var hOffset = opts.offset !== undefined ? opts.offset : DEFAULT_OFFSET;
    var vProp = _vProp(placement);
    /* top/bottom 初始值（后续 _reflow 会更新） */
    el.style.cssText = _hStyle(placement, hOffset);
    el.style[vProp] = hOffset + 'px';

    /* content group */
    var content = document.createElement('div');
    content.className = 'dsm-toast__content';

    if (hasIcon) {
      var icon = document.createElement('span');
      icon.className = 'dsm-toast__icon';
      icon.innerHTML = ICONS[tone] || '';
      content.appendChild(icon);
    }

    var msg = document.createElement('p');
    msg.className = 'dsm-toast__message';
    msg.textContent = opts.message || '';
    content.appendChild(msg);
    el.appendChild(content);

    /* action */
    if (hasAction) {
      var action = document.createElement('button');
      action.type = 'button';
      action.className = 'dsm-toast__action';
      action.textContent = opts.action;
      action.addEventListener('click', function () {
        if (typeof opts.onAction === 'function') opts.onAction();
        dismiss(id);
      });
      el.appendChild(action);
    }

    /* close */
    if (showClose) {
      var closeBtn = document.createElement('button');
      closeBtn.type = 'button';
      closeBtn.className = 'dsm-toast__close';
      closeBtn.setAttribute('aria-label', 'Dismiss notification');
      closeBtn.innerHTML = CLOSE_ICON;
      closeBtn.addEventListener('click', function () {
        dismiss(id);
      });
      el.appendChild(closeBtn);
    }

    /* 进入动画结束后移除 --entering 类，避免动画持续占用 will-change */
    el.addEventListener('animationend', function _onEnter(e) {
      if (e.animationName && e.animationName.indexOf('dsm-toast-in') === 0) {
        el.classList.remove('dsm-toast--entering');
        el.removeEventListener('animationend', _onEnter);
      }
    });

    return el;
  }

  /* ── 计时器 ── */
  function _startTimer(entry) {
    if (!entry || entry.remaining <= 0) return;
    entry.startedAt = Date.now();
    entry.timerId = setTimeout(function () {
      dismiss(entry.id);
    }, entry.remaining);
  }

  function _pauseTimer(entry) {
    if (!entry || !entry.timerId) return;
    clearTimeout(entry.timerId);
    entry.timerId = null;
    entry.remaining = Math.max(0, entry.remaining - (Date.now() - entry.startedAt));
  }

  function _resumeTimer(entry) {
    if (!entry || entry.timerId) return;
    if (entry.remaining > 0) _startTimer(entry);
  }

  /* ── 找 entry by id（跨所有 placement）── */
  function _findEntry(id) {
    for (var p in _instances) {
      var list = _instances[p];
      for (var i = 0; i < list.length; i++) {
        if (list[i].id === id) return {
          entry: list[i],
          list: list,
          placement: p,
          idx: i
        };
      }
    }
    return null;
  }

  /* ── 公开 API：show ── */
  function show(opts) {
    if (!opts || !opts.message) return '';

    /* 规范化 placement */
    var placement = opts.placement || DEFAULT_PLACEMENT;
    if (VALID_PLACEMENTS.indexOf(placement) === -1) {
      console.warn('[DsmToast] invalid placement "' + placement + '", fallback to "' + DEFAULT_PLACEMENT + '"');
      placement = DEFAULT_PLACEMENT;
    }
    opts = Object.assign({}, opts, {
      placement: placement
    });

    /* grouping：相同 message + tone 则复用，计数器 +1 */
    if (opts.grouping) {
      var list = _getList(placement);
      for (var g = 0; g < list.length; g++) {
        var ex = list[g];
        if (ex.opts.message === opts.message && (ex.opts.tone || 'default') === (opts.tone || 'default')) {
          /* 更新计数 badge */
          ex.repeatNum = (ex.repeatNum || 1) + 1;
          var badge = ex.el.querySelector('.dsm-toast__badge');
          if (!badge) {
            badge = document.createElement('span');
            badge.className = 'dsm-toast__badge';
            ex.el.insertBefore(badge, ex.el.firstChild);
          }
          badge.textContent = String(ex.repeatNum);
          /* 重置计时器 */
          _pauseTimer(ex);
          _startTimer(ex);
          return ex.id;
        }
      }
    }

    /* appendTo */
    var container = _resolveContainer(opts.appendTo);

    _idCounter += 1;
    var id = String(_idCounter);
    var el = _buildEl(id, opts, placement);

    /* 计算 duration */
    var tone = opts.tone || 'default';
    var duration = opts.duration !== undefined ?
      opts.duration :
      (opts.action ? Math.max(5000, DEFAULT_DURATION[tone] || 3000) : (DEFAULT_DURATION[tone] || 3000));

    var entry = {
      id: id,
      el: el,
      height: 0,
      offset: opts.offset !== undefined ? opts.offset : DEFAULT_OFFSET,
      opts: opts,
      timerId: null,
      remaining: duration,
      startedAt: 0,
      repeatNum: 1,
      ro: null
    };

    /* 追加到 DOM + 实例列表 */
    container.appendChild(el);
    _getList(placement).push(entry);

    /* 首次 layout 后 _reflow（等 DOM paint 再算高度） */
    requestAnimationFrame(function () {
      entry.height = el.getBoundingClientRect().height || 44;
      _reflow(placement);

      /* ResizeObserver：toast 内容变化时重算高度并重排 */
      if (typeof ResizeObserver !== 'undefined') {
        var ro = new ResizeObserver(function () {
          var newH = el.getBoundingClientRect().height;
          if (newH && newH !== entry.height) {
            entry.height = newH;
            _reflow(placement);
          }
        });
        ro.observe(el);
        entry.ro = ro;
      }

      /* 计时 */
      if (duration > 0) _startTimer(entry);
    });

    /* hover / focus / touch 暂停 */
    el.addEventListener('mouseenter', function () {
      _pauseTimer(entry);
    });
    el.addEventListener('mouseleave', function () {
      _resumeTimer(entry);
    });
    el.addEventListener('focusin', function () {
      _pauseTimer(entry);
    });
    el.addEventListener('focusout', function () {
      _resumeTimer(entry);
    });
    el.addEventListener('touchstart', function () {
      _pauseTimer(entry);
    }, {
      passive: true
    });
    el.addEventListener('touchend', function () {
      _resumeTimer(entry);
    }, {
      passive: true
    });

    el.dispatchEvent(new CustomEvent('dsm:toast:show', {
      bubbles: true,
      detail: {
        id: id,
        options: opts
      }
    }));

    return id;
  }

  /* ── 公开 API：dismiss ── */
  function dismiss(id) {
    var found = _findEntry(id);
    if (!found) return;

    var entry = found.entry;
    var list = found.list;
    var placement = found.placement;

    /* 停计时 */
    if (entry.timerId) {
      clearTimeout(entry.timerId);
      entry.timerId = null;
    }

    /* 停 ResizeObserver */
    if (entry.ro) {
      entry.ro.disconnect();
      entry.ro = null;
    }

    /* 退出动画 */
    entry.el.classList.add('dsm-toast--exiting');

    setTimeout(function () {
      if (entry.el.parentNode) entry.el.parentNode.removeChild(entry.el);

      /* 从列表移除 */
      var idx = list.indexOf(entry);
      if (idx !== -1) list.splice(idx, 1);

      /* 重排后续 toast */
      _reflow(placement);

      if (typeof opts.onClose === 'function') {
        try {
          entry.opts.onClose();
        } catch (e) {}
      }

      entry.el.dispatchEvent(new CustomEvent('dsm:toast:dismiss', {
        bubbles: true,
        detail: {
          id: id
        }
      }));
    }, EXIT_MS);
  }

  /* ── 公开 API：closeAll ── */
  function closeAll(placement) {
    if (placement) {
      var list = (_instances[placement] || []).slice();
      list.forEach(function (e) {
        dismiss(e.id);
      });
    } else {
      for (var p in _instances) {
        var all = _instances[p].slice();
        all.forEach(function (e) {
          dismiss(e.id);
        });
      }
    }
  }

  return {
    show: show,
    dismiss: dismiss,
    closeAll: closeAll
  };
})();

window.DsmToast = DsmToast;

window.DsmDialog = DsmDialog;

/* ============================================================
   DsmTabs — 选项卡控制器
   Figma: https://www.figma.com/design/DaaNEpELVLq1oWo540GsFW/DSM?node-id=2316-5600

   功能：
     - 点击切换激活项，更新 aria-selected / is-active / tabindex
     - 左右方向键 + Home/End 键盘导航（roving tabindex）
     - 自动切换关联面板（aria-controls → 对应 panel 的 hidden 属性）
     - 移动端溢出时，选中项自动滚动到可见区域（居中）

   API：
     DsmTabs.init(root?)       — 初始化（root 可选，默认 document）
     DsmTabs.activate(target)  — 激活某个 tab（传 DOM 元素或 value 字符串）

   HTML 约定（由 dsm-tabs.liquid 生成）：
     <div class="dsm-tabs" role="tablist" data-dsm-tabs="id">
       <button class="dsm-tabs__item" role="tab"
               data-dsm-tab-value="value"
               aria-controls="id-panel-value" …>
     </div>

   自定义事件（在 tablist 上派发，bubbles）：
     dsm:tabs:change  →  detail: { value, tab, tabs, index }
   ============================================================ */

const DsmTabs = (() => {
  'use strict';

  function _toNumber(value) {
    var n = parseInt(value, 10);
    return Number.isNaN(n) ? null : n;
  }

  function _getEnabledTabs(list) {
    return Array.prototype.slice.call(
      list.querySelectorAll('[role="tab"]:not([aria-disabled="true"]):not([disabled])')
    );
  }

  function _getScrollContainer(list) {
    var rail = list.querySelector('[data-dsm-tabs-scroll]');
    return rail || list;
  }

  function _getStickyTop(list) {
    var node = list && list.nodeType === 1 ? list : document.documentElement;

    while (node && node.nodeType === 1) {
      var value = getComputedStyle(node).getPropertyValue('--sticky-top');
      var parsed = _toNumber(value);
      if (parsed != null) return parsed;
      node = node.parentElement;
    }

    var rootValue = getComputedStyle(document.documentElement).getPropertyValue('--sticky-top');
    var rootNumber = _toNumber(rootValue);
    return rootNumber == null ? 0 : rootNumber;
  }

  function _getScrollOffset(list) {
    var customOffset = _toNumber(list.getAttribute('data-dsm-tabs-offset'));
    if (customOffset != null) return customOffset;
    return list.offsetHeight + _getStickyTop(list);
  }

  function _resolveAnchorTarget(id) {
    if (!id) return null;

    var exact = document.getElementById(id);
    if (exact) return exact;

    var parts = id.split('__');
    var suffix = parts.length > 1 ? parts.slice(1).join('__') : '';
    if (!suffix) return null;

    return document.querySelector('[id$="__' + CSS.escape(suffix) + '"]');
  }

  /* ── 切换面板显隐 ── */
  function _syncPanels(list, activeTab, tabs) {
    tabs.forEach(function (t) {
      var panelId = t.getAttribute('aria-controls');
      if (!panelId) return;
      var panel = document.getElementById(panelId);
      if (panel) panel.hidden = (t !== activeTab);
    });
  }

  /* ── 溢出时让选中项滚动到可见区域（优先居中） ── */
  function _scrollIntoView(list, tab) {
    var scroller = _getScrollContainer(list);
    if (scroller.scrollWidth <= scroller.clientWidth) return;
    var target = tab.offsetLeft - (scroller.clientWidth / 2) + (tab.offsetWidth / 2);
    if (typeof scroller.scrollTo === 'function') {
      try {
        scroller.scrollTo({
          left: target,
          behavior: 'smooth'
        });
      } catch (e) {
        scroller.scrollLeft = target;
      }
    } else {
      scroller.scrollLeft = target;
    }
  }

  /* ── 激活指定 tab ── */
  function _activate(list, tab) {
    var tabs = Array.prototype.slice.call(list.querySelectorAll('[role="tab"]'));
    if (tabs.indexOf(tab) === -1) return;
    var behavior = list.getAttribute('data-dsm-tabs-behavior') || 'panel';

    tabs.forEach(function (t) {
      var active = (t === tab);
      t.setAttribute('aria-selected', String(active));
      t.classList.toggle('is-active', active);
      t.tabIndex = active ? 0 : -1;
    });

    if (behavior !== 'anchor') {
      _syncPanels(list, tab, tabs);
    }
    _scrollIntoView(list, tab);
    _syncOverflowArrowsState(list);

    list.dispatchEvent(new CustomEvent('dsm:tabs:change', {
      bubbles: true,
      detail: {
        value: tab.getAttribute('data-dsm-tab-value'),
        tab: tab,
        tabs: tabs,
        index: tabs.indexOf(tab)
      }
    }));
  }

  function _setActiveByIndex(list, tabs, index, options) {
    if (index < 0 || index >= tabs.length) return;
    var next = tabs[index];
    if (!next) return;
    _activate(list, next);
    if (options && options.smooth) {
      _scrollIntoView(list, next);
    }
  }

  function _syncOverflowArrowsState(list) {
    var nav = list.querySelector('[data-dsm-tabs-overflow-nav]');
    if (!nav) return;

    var prevBtn = nav.querySelector('[data-dsm-tabs-prev]');
    var nextBtn = nav.querySelector('[data-dsm-tabs-next]');
    if (!prevBtn || !nextBtn) return;

    var tabs = _getEnabledTabs(list);
    if (!tabs.length) {
      prevBtn.disabled = true;
      nextBtn.disabled = true;
      nav.classList.remove('is-overflowing');
      nav.classList.add('is-at-start', 'is-at-end');
      return;
    }

    var scroller = _getScrollContainer(list);
    var isOverflowing = scroller.scrollWidth > scroller.clientWidth + 1;
    nav.classList.toggle('is-overflowing', isOverflowing);

    var activeTab = list.querySelector('[role="tab"].is-active') || tabs[0];
    var index = tabs.indexOf(activeTab);
    if (index < 0) index = 0;

    var atStart = !isOverflowing || index <= 0;
    var atEnd = !isOverflowing || index >= tabs.length - 1;
    nav.classList.toggle('is-at-start', atStart);
    nav.classList.toggle('is-at-end', atEnd);

    prevBtn.disabled = atStart;
    nextBtn.disabled = atEnd;
  }

  function _activateByArrow(list, tab) {
    if (!tab) return;
    var behavior = list.getAttribute('data-dsm-tabs-behavior') || 'panel';
    if (behavior === 'anchor' && typeof list._dsmAnchorJump === 'function') {
      list._dsmAnchorJump(tab);
    } else {
      _activate(list, tab);
    }
  }

  function _getAnchorTargets(list, tabs) {
    return tabs.map(function (tab) {
      var href = tab.getAttribute('href') || '';
      if (!href || href.charAt(0) !== '#') return null;
      var id = href.slice(1);
      return id ? _resolveAnchorTarget(id) : null;
    });
  }

  function _bindAnchorMode(list) {
    var tabs = Array.prototype.slice.call(
      list.querySelectorAll('[role="tab"]:not([aria-disabled="true"]):not([disabled])')
    );
    if (!tabs.length) return;

    var targets = _getAnchorTargets(list, tabs);
    var state = {
      isProgrammaticScrolling: false,
      scrollEndTimer: null,
      fallbackTimer: null,
      onScroll: null,
      onResize: null,
      stickyObserver: null,
      stickySentinel: null
    };
    list._dsmAnchorState = state;

    function setActiveFromScroll() {
      if (state.isProgrammaticScrolling) return;
      var offset = _getScrollOffset(list);
      var activeIndex = 0;

      for (var i = 0; i < targets.length; i++) {
        var target = targets[i];
        if (!target) continue;
        var top = target.getBoundingClientRect().top - offset;
        if (top <= 1) {
          activeIndex = i;
        }
      }

      var lastIndex = targets.length - 1;
      var lastTarget = targets[lastIndex];
      if (lastTarget) {
        var rect = lastTarget.getBoundingClientRect();
        if (rect.top < window.innerHeight && rect.bottom <= window.innerHeight + 2) {
          activeIndex = lastIndex;
        }
      }

      _setActiveByIndex(list, tabs, activeIndex, {
        smooth: false
      });
    }

    state.onScroll = function () {
      if (state.isProgrammaticScrolling || state._ticking) return;
      state._ticking = true;
      requestAnimationFrame(function () {
        state._ticking = false;
        setActiveFromScroll();
      });
    };

    state.onResize = function () {
      targets = _getAnchorTargets(list, tabs);
      setActiveFromScroll();
    };

    document.addEventListener('scroll', state.onScroll, {
      passive: true
    });
    window.addEventListener('resize', state.onResize, {
      passive: true
    });

    list._dsmAnchorJump = function (tab) {
      var href = tab.getAttribute('href') || '';
      if (!href || href.charAt(0) !== '#') return;
      var target = _resolveAnchorTarget(href.slice(1));
      if (!target) return;
      var headerEl = document.querySelector('.t4s-section-header');

      state.isProgrammaticScrolling = true;
      _activate(list, tab);
      if (headerEl) {
        headerEl.dispatchEvent(new Event('preventHeaderReveal'));
      }

      window.scrollTo({
        top: target.getBoundingClientRect().top + window.scrollY - _getScrollOffset(list),
        behavior: 'smooth'
      });

      clearTimeout(state.scrollEndTimer);
      clearTimeout(state.fallbackTimer);
      var unlock = function () {
        clearTimeout(state.scrollEndTimer);
        state.scrollEndTimer = setTimeout(function () {
          document.removeEventListener('scroll', unlock, {
            passive: true
          });
          state.isProgrammaticScrolling = false;
          setActiveFromScroll();
        }, 150);
      };
      document.addEventListener('scroll', unlock, {
        passive: true
      });

      state.fallbackTimer = setTimeout(function () {
        document.removeEventListener('scroll', unlock, {
          passive: true
        });
        state.isProgrammaticScrolling = false;
        setActiveFromScroll();
      }, 2000);
    };

    if (list.getAttribute('data-dsm-tabs-sticky') === 'true' && typeof IntersectionObserver === 'function') {
      var sentinel = document.createElement('span');
      sentinel.className = 'dsm-tabs__sticky-sentinel';
      sentinel.setAttribute('aria-hidden', 'true');
      list.parentNode.insertBefore(sentinel, list);
      state.stickySentinel = sentinel;

      state.stickyObserver = new IntersectionObserver(
        function (entries) {
          var entry = entries[0];
          if (!entry) return;
          list.classList.toggle('is-stuck', entry.intersectionRatio === 0);
        }, {
          root: null,
          threshold: [0, 1],
          rootMargin: '-' + _getStickyTop(list) + 'px 0px 0px 0px'
        }
      );
      state.stickyObserver.observe(sentinel);
    }

    setActiveFromScroll();
  }

  function _unbindAnchorMode(list) {
    var state = list._dsmAnchorState;
    if (!state) return;

    if (state.onScroll) {
      document.removeEventListener('scroll', state.onScroll);
    }
    if (state.onResize) {
      window.removeEventListener('resize', state.onResize);
    }
    if (state.stickyObserver) {
      state.stickyObserver.disconnect();
    }
    if (state.stickySentinel && state.stickySentinel.parentNode) {
      state.stickySentinel.parentNode.removeChild(state.stickySentinel);
    }
    clearTimeout(state.scrollEndTimer);
    clearTimeout(state.fallbackTimer);

    list._dsmAnchorState = null;
    list._dsmAnchorJump = null;
  }

  /* ── 溢出渐隐遮罩：根据滚动位置显隐左右遮罩 ── */
  function _bindScrollFade(list, wrapper) {
    var scroller = _getScrollContainer(list);

    function update() {
      if (scroller.scrollWidth <= scroller.clientWidth) {
        wrapper.classList.remove('has-overflow-start', 'has-overflow-end');
        return;
      }
      var threshold = 2;
      wrapper.classList.toggle('has-overflow-start', scroller.scrollLeft > threshold);
      wrapper.classList.toggle(
        'has-overflow-end',
        scroller.scrollLeft + scroller.clientWidth < scroller.scrollWidth - threshold
      );
    }
    scroller.addEventListener('scroll', update, {
      passive: true
    });
    requestAnimationFrame(update);
    if (typeof ResizeObserver === 'function') {
      new ResizeObserver(update).observe(scroller);
    }
  }

  /* ── 鼠标拖拽横向滚动（桌面端，触屏原生支持无需处理） ── */
  function _bindDragScroll(list) {
    var scroller = _getScrollContainer(list);
    var startX = 0;
    var scrollStart = 0;
    var dragging = false;
    var DRAG_THRESHOLD = 5; /* px，超过阈值才算拖拽，避免普通点击误判 */

    function onMouseMove(e) {
      if (!dragging) return;
      var dx = e.pageX - startX;
      if (Math.abs(dx) > DRAG_THRESHOLD) {
        list._dsmDragged = true;
      }
      scroller.scrollLeft = scrollStart - dx;
    }

    function onMouseUp() {
      if (!dragging) return;
      dragging = false;
      scroller.style.cursor = '';
      scroller.style.userSelect = '';
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    }

    scroller.addEventListener('mousedown', function (e) {
      /* 仅主键 */
      if (e.button !== 0) return;
      /* 无需拖拽时跳过 */
      if (scroller.scrollWidth <= scroller.clientWidth) return;

      dragging = true;
      list._dsmDragged = false;
      startX = e.pageX;
      scrollStart = scroller.scrollLeft;
      scroller.style.cursor = 'grabbing';
      scroller.style.userSelect = 'none';
      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
    });
  }

  /* ── 绑定一个 tablist ── */
  function _bind(list) {
    if (list.getAttribute('data-dsm-tabs-bound') === '1') return;
    list.setAttribute('data-dsm-tabs-bound', '1');

    list.addEventListener('click', function (e) {
      /* 拖拽结束后忽略本次 click，防止误触 */
      if (list._dsmDragged) {
        list._dsmDragged = false;
        e.preventDefault();
        return;
      }
      var tab = e.target.closest('[role="tab"]');
      if (!tab || !list.contains(tab)) return;

      var isDisabled = tab.getAttribute('aria-disabled') === 'true' || tab.disabled;
      if (isDisabled) {
        e.preventDefault();
        return;
      }

      var href = tab.getAttribute('href');
      var isNavLink = !!href && href !== '#' && href.charAt(0) !== '#';
      var behavior = list.getAttribute('data-dsm-tabs-behavior') || 'panel';
      var isAnchorHash = !!href && href.charAt(0) === '#';

      if (behavior === 'anchor' && isAnchorHash) {
        e.preventDefault();
        if (typeof list._dsmAnchorJump === 'function') {
          list._dsmAnchorJump(tab);
        } else {
          _activate(list, tab);
        }
        return;
      }

      if (!isNavLink) e.preventDefault();
      _activate(list, tab);
    });

    var overflowNav = list.querySelector('[data-dsm-tabs-overflow-nav]');
    if (overflowNav) {
      overflowNav.addEventListener('click', function (e) {
        var prev = e.target.closest('[data-dsm-tabs-prev]');
        var next = e.target.closest('[data-dsm-tabs-next]');
        if (!prev && !next) return;

        var tabs = _getEnabledTabs(list);
        if (!tabs.length) return;

        var activeTab = list.querySelector('[role="tab"].is-active') || tabs[0];
        var index = tabs.indexOf(activeTab);
        if (index < 0) index = 0;

        if (prev && index > 0) {
          _activateByArrow(list, tabs[index - 1]);
        }
        if (next && index < tabs.length - 1) {
          _activateByArrow(list, tabs[index + 1]);
        }
      });
    }

    /* ── 鼠标拖拽横向滚动（桌面端） ── */
    _bindDragScroll(list);

    /* ── 胶囊型溢出渐隐遮罩 ── */
    var scrollWrap = list.querySelector('[data-dsm-tabs-scroll]');
    if (scrollWrap) {
      _bindScrollFade(list, scrollWrap);
    }

    _getScrollContainer(list).addEventListener('scroll', function () {
      _syncOverflowArrowsState(list);
    }, {
      passive: true
    });

    if (typeof ResizeObserver === 'function') {
      new ResizeObserver(function () {
        _syncOverflowArrowsState(list);
      }).observe(_getScrollContainer(list));
    }

    /* ── 初始化：让已选中项滚动到可见区域 ── */
    var activeTab = list.querySelector('[role="tab"].is-active');
    if (activeTab) {
      /* 延迟到下帧，确保布局已完成 */
      requestAnimationFrame(function () {
        _scrollIntoView(list, activeTab);
        _syncOverflowArrowsState(list);
      });
    } else {
      _syncOverflowArrowsState(list);
    }

    if ((list.getAttribute('data-dsm-tabs-behavior') || 'panel') === 'anchor') {
      _bindAnchorMode(list);
    }

    list.addEventListener('keydown', function (e) {
      var tabs = _getEnabledTabs(list);
      if (!tabs.length) return;

      var current = document.activeElement;
      var idx = tabs.indexOf(current);
      if (idx === -1) return;

      var next = null;
      switch (e.key) {
        case 'ArrowRight':
          next = tabs[(idx + 1) % tabs.length];
          break;
        case 'ArrowLeft':
          next = tabs[(idx - 1 + tabs.length) % tabs.length];
          break;
        case 'Home':
          next = tabs[0];
          break;
        case 'End':
          next = tabs[tabs.length - 1];
          break;
        default:
          return;
      }

      e.preventDefault();
      next.focus();
      var behavior = list.getAttribute('data-dsm-tabs-behavior') || 'panel';
      if (behavior === 'anchor' && typeof list._dsmAnchorJump === 'function') {
        list._dsmAnchorJump(next);
      } else {
        _activate(list, next);
      }
    });
  }

  /* ── 初始化 ── */
  function init(root) {
    var scope = root || document;
    var lists = scope.querySelectorAll ? scope.querySelectorAll('[data-dsm-tabs]') : [];
    Array.prototype.forEach.call(lists, _bind);
  }

  function destroy(root) {
    var scope = root || document;
    var lists = scope.querySelectorAll ? scope.querySelectorAll('[data-dsm-tabs]') : [];
    Array.prototype.forEach.call(lists, function (list) {
      _unbindAnchorMode(list);
      list.removeAttribute('data-dsm-tabs-bound');
    });
  }

  /* ── 公开 activate：接受 DOM 元素或 value 字符串 ── */
  function activate(target, root) {
    var scope = root || document;
    var tab = null;

    if (typeof target === 'string') {
      tab = scope.querySelector('[data-dsm-tabs] [data-dsm-tab-value="' + target + '"]');
    } else if (target && target.nodeType === 1) {
      tab = target;
    }
    if (!tab) return;

    var list = tab.closest('[data-dsm-tabs]');
    if (list) _activate(list, tab);
  }

  /* ── 自动初始化 ── */
  function _autoInit() {
    init(document);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', _autoInit);
  } else {
    _autoInit();
  }

  return {
    init: init,
    destroy: destroy,
    activate: activate
  };
})();

if (typeof window !== 'undefined') {
  window.DsmTabs = DsmTabs;
}

/* ============================================================
   DsmDatepicker — 日期选择器
   Figma: https://www.figma.com/design/KjnThc3PVmaCm0Dhv5NyzR/SEO?node-id=303-3823

   行为：
     - 点击字段开合面板；Desktop 为内联面板，Mobile ≤1023 为弹窗（同一 DOM，CSS 切换）
     - 选中某天 → 立即回填输入框（需求：选择后日期显示在输入框中），面板保持展开
     - 确认 / 关闭按钮 / ESC / 点击遮罩或面板外 → 收起
     - 支持 min / max 限制；日期格式统一 YYYY/MM/DD（与需求一致）

   API：
     DsmDatepicker.init(scope)              → 初始化 scope 内所有 [data-dsm-datepicker]
     DsmDatepicker.getValue(id)             → 'YYYY/MM/DD' | ''
     DsmDatepicker.setValue(id, 'YYYY/MM/DD') → void
     DsmDatepicker.open(id) / close(id)     → void
     DsmDatepicker.destroy(id)              → 解绑

   HTML 触发（无需手写 JS，自动初始化）：
     <div data-dsm-datepicker data-dp-id="lmp"></div>

   自定义事件：
     dsm:datepicker:change   detail: { id, value }
     dsm:datepicker:open     detail: { id }
     dsm:datepicker:close    detail: { id }

   注意：不使用 JS 判断视口做布局，布局全交给 CSS media query；
        仅用 matchMedia 判断「当前是否弹窗模式」以决定是否需要焦点陷阱。
   ============================================================ */

const DsmDatepicker = (() => {
  const SELECTOR = '[data-dsm-datepicker]';
  const DESKTOP_QUERY = '(min-width: 1024px)';
  const instances = new Map();

  const pad2 = (n) => (n < 10 ? '0' + n : String(n));
  const fmt = (d) => d.getFullYear() + '/' + pad2(d.getMonth() + 1) + '/' + pad2(d.getDate());
  const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  /** 解析 'YYYY/MM/DD' 或 'YYYY-MM-DD' → Date | null（按本地时间，避免时区偏移） */
  function parse(str) {
    if (!str) return null;
    const m = String(str).trim().match(/^(\d{4})[/-](\d{1,2})[/-](\d{1,2})$/);
    if (!m) return null;
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    return isNaN(d.getTime()) ? null : d;
  }

  const isDesktop = () => typeof window.matchMedia === 'function' && window.matchMedia(DESKTOP_QUERY).matches;

  function sameDay(a, b) {
    return !!a && !!b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
  }

  /* ── 单个实例 ── */
  function create(root) {
    const id = root.dataset.dpId || root.id || 'dsm-datepicker';
    if (instances.has(id)) return instances.get(id);

    const field = root.querySelector('.js-dsm-dp-toggle');
    const panel = root.querySelector('[data-dp-panel]');
    const overlay = root.querySelector('[data-dp-overlay]');
    const input = root.querySelector('[data-dp-input]');
    const display = root.querySelector('[data-dp-display]');
    const monthEl = root.querySelector('[data-dp-month]');
    const daysEl = root.querySelector('[data-dp-days]');
    const closeBtn = root.querySelector('.js-dsm-dp-close');
    const confirmBtn = root.querySelector('.js-dsm-dp-confirm');
    if (!field || !panel || !daysEl) return null;

    const weekStart = Number(root.dataset.dpWeekStart || 0);
    const minDate = parse(root.dataset.dpMin);
    const maxDate = parse(root.dataset.dpMax);
    const placeholder = display && display.classList.contains('dsm-datepicker__value--empty') ? display.textContent.trim() : '';

    const state = {
      value: parse(input ? input.value : ''),
      view: parse(input ? input.value : '') || new Date(),
      open: false,
      lastFocus: null,
    };
    if (!state.value) state.view = new Date(state.view.getFullYear(), state.view.getMonth(), 1);

    const outOfRange = (d) => (minDate && d < new Date(minDate.getFullYear(), minDate.getMonth(), minDate.getDate())) || (maxDate && d > new Date(maxDate.getFullYear(), maxDate.getMonth(), maxDate.getDate()));

    /* ── 渲染 ── */
    function render() {
      const y = state.view.getFullYear();
      const m = state.view.getMonth();
      monthEl.textContent = MONTHS[m] + ' ' + y;

      const first = new Date(y, m, 1);
      // 网格起点：本月 1 号所在周的 weekStart
      const startOffset = (first.getDay() - weekStart + 7) % 7;
      const gridStart = new Date(y, m, 1 - startOffset);
      const today = new Date();

      const frag = document.createDocumentFragment();
      for (let i = 0; i < 42; i++) {
        const d = new Date(gridStart.getFullYear(), gridStart.getMonth(), gridStart.getDate() + i);
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'dsm-datepicker__day';
        btn.textContent = String(d.getDate());
        btn.dataset.date = fmt(d);
        btn.setAttribute('role', 'gridcell');
        if (d.getMonth() !== m) btn.classList.add('dsm-datepicker__day--muted');
        if (sameDay(d, today)) {
          btn.classList.add('dsm-datepicker__day--today');
          btn.setAttribute('aria-current', 'date');
        }
        if (sameDay(d, state.value)) {
          btn.classList.add('dsm-datepicker__day--selected');
          btn.setAttribute('aria-selected', 'true');
        }
        if (outOfRange(d)) {
          btn.classList.add('dsm-datepicker__day--disabled');
          btn.disabled = true;
        }
        frag.appendChild(btn);
      }
      daysEl.textContent = '';
      daysEl.appendChild(frag);
    }

    /* ── 回填 ── */
    function commit(d) {
      state.value = d;
      const str = d ? fmt(d) : '';
      if (input) input.value = str;
      if (display) {
        display.textContent = str || placeholder;
        display.classList.toggle('dsm-datepicker__value--empty', !str);
      }
      render();
      root.dispatchEvent(new CustomEvent('dsm:datepicker:change', { detail: { id, value: str }, bubbles: true }));
    }

    /* ── 开合 ── */
    function open() {
      if (state.open) return;
      state.open = true;
      state.lastFocus = document.activeElement;
      root.classList.add('dsm-datepicker--open');
      panel.hidden = false;
      if (overlay) overlay.hidden = false;
      field.setAttribute('aria-expanded', 'true');
      render();
      const firstDay = daysEl.querySelector('.dsm-datepicker__day--selected, .dsm-datepicker__day--today, .dsm-datepicker__day');
      if (firstDay) firstDay.focus({ preventScroll: true });
      root.dispatchEvent(new CustomEvent('dsm:datepicker:open', { detail: { id }, bubbles: true }));
    }

    function close() {
      if (!state.open) return;
      state.open = false;
      root.classList.remove('dsm-datepicker--open');
      panel.hidden = true;
      if (overlay) overlay.hidden = true;
      field.setAttribute('aria-expanded', 'false');
      if (state.lastFocus && state.lastFocus.focus) state.lastFocus.focus({ preventScroll: true });
      root.dispatchEvent(new CustomEvent('dsm:datepicker:close', { detail: { id }, bubbles: true }));
    }

    /* ── 事件 ── */
    const onField = () => (state.open ? close() : open());
    const onNav = (e) => {
      const dir = e.target.closest('[data-dp-nav]');
      if (!dir) return;
      const y = state.view.getFullYear();
      const m = state.view.getMonth();
      const step = { 'prev-year': [y - 1, m], 'prev-month': [y, m - 1], 'next-month': [y, m + 1], 'next-year': [y + 1, m] }[dir.dataset.dpNav];
      if (!step) return;
      state.view = new Date(step[0], step[1], 1);
      render();
    };
    const onDays = (e) => {
      const btn = e.target.closest('.dsm-datepicker__day');
      if (!btn || btn.disabled) return;
      const d = parse(btn.dataset.date);
      if (d) commit(d);
    };
    const onDocClick = (e) => {
      if (!state.open) return;
      if (root.contains(e.target)) return;
      // 弹窗模式下遮罩在 root 内，这里只处理 Desktop 内联面板的「点外面收起」
      close();
    };
    const onKey = (e) => {
      if (!state.open) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        close();
        return;
      }
      // 弹窗模式：Tab 焦点陷阱
      if (e.key === 'Tab' && !isDesktop()) {
        const focusables = panel.querySelectorAll('button:not([disabled]), [href], input, [tabindex]:not([tabindex="-1"])');
        if (!focusables.length) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    field.addEventListener('click', onField);
    panel.addEventListener('click', (e) => {
      if (e.target.closest('[data-dp-nav]')) onNav(e);
      else if (e.target.closest('.dsm-datepicker__day')) onDays(e);
    });
    if (closeBtn) closeBtn.addEventListener('click', close);
    if (confirmBtn) confirmBtn.addEventListener('click', close);
    if (overlay) overlay.addEventListener('click', close);
    document.addEventListener('click', onDocClick);
    document.addEventListener('keydown', onKey);

    const api = {
      id,
      open,
      close,
      getValue: () => (state.value ? fmt(state.value) : ''),
      setValue: (v) => {
        const d = parse(v);
        if (d) {
          state.value = d;
          state.view = new Date(d.getFullYear(), d.getMonth(), 1);
          commit(d);
        }
      },
      destroy: () => {
        field.removeEventListener('click', onField);
        document.removeEventListener('click', onDocClick);
        document.removeEventListener('keydown', onKey);
        instances.delete(id);
      },
    };
    instances.set(id, api);
    render();
    return api;
  }

  /** 初始化 scope 内所有 datepicker（幂等） */
  function init(scope) {
    const ctx = scope || document;
    ctx.querySelectorAll(SELECTOR).forEach((el) => {
      const id = el.dataset.dpId || el.id;
      if (!id || !instances.has(id)) create(el);
    });
  }

  const byId = (id) => instances.get(id);

  function _autoInit() {
    init(document);
  }
  if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', _autoInit);
    else _autoInit();
  }

  return {
    init,
    open: (id) => { const i = byId(id); if (i) i.open(); },
    close: (id) => { const i = byId(id); if (i) i.close(); },
    getValue: (id) => { const i = byId(id); return i ? i.getValue() : ''; },
    setValue: (id, v) => { const i = byId(id); if (i) i.setValue(v); },
    destroy: (id) => { const i = byId(id); if (i) i.destroy(); },
  };
})();

if (typeof window !== 'undefined') {
  window.DsmDatepicker = DsmDatepicker;
}
